import React, { useState } from 'react';
import { Building2, Percent, Hash, Users as UsersIcon, RotateCcw, Save, UserPlus, Trash2, Stamp, Package, Pencil, Plus, KeyRound, Loader2 } from 'lucide-react';
import { useData } from '../context/DataContext';
import { PageHeader, StatusBadge, Card } from '../components/Shared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ImageUploadField, SignatureField } from '../components/BrandingFields';
import { useAuth, formatApiError } from '../context/AuthContext';
import { fmtRM2, NUMBER_TYPES, buildNumber } from '../lib/store';
import { toast } from 'sonner';

const ROLES = ['Administrator', 'Finance / Accounts', 'Sales / Admin'];
const emptyItem = { name: '', description: '', unit: 'unit', price: 0, taxable: true };

export default function Settings() {
  const data = useData();
  const { company, updateCompany, users, products, addItem, updateItem, removeItem, reset } = data;
  const { user, changePassword } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);
  const [form, setForm] = useState(company);
  const [userOpen, setUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'Sales / Admin', status: 'invited' });
  const [itemOpen, setItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [itemEditId, setItemEditId] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setNext = (type, raw) => {
    const n = raw === '' ? undefined : Math.max(1, Number(raw) || 1);
    setForm((f) => {
      const nextNumbers = { ...(f.nextNumbers || {}) };
      if (n === undefined) delete nextNumbers[type]; else nextNumbers[type] = n;
      return { ...f, nextNumbers };
    });
  };

  const sampleNumber = (type) => buildNumber(form, type, data[type] || []);

  const saveCompany = () => { updateCompany(form); toast.success('Company settings saved'); };

  const saveBranding = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    updateCompany(patch);
  };

  const addUser = () => {
    if (!newUser.name.trim() || !newUser.email.trim()) { toast.error('Name and email are required'); return; }
    addItem('users', newUser);
    toast.success('User invited');
    setNewUser({ name: '', email: '', role: 'Sales / Admin', status: 'invited' });
    setUserOpen(false);
  };

  const openNewItem = () => { setItemForm(emptyItem); setItemEditId(null); setItemOpen(true); };  const openEditItem = (p) => { setItemForm(p); setItemEditId(p.id); setItemOpen(true); };
  const saveItem = () => {
    if (!itemForm.name.trim()) { toast.error('Item name is required'); return; }
    const payload = { ...itemForm, price: Number(itemForm.price) || 0 };
    if (itemEditId) { updateItem('products', itemEditId, payload); toast.success('Item updated'); }
    else { addItem('products', payload); toast.success('Item added — it is now selectable in every document'); }
    setItemOpen(false);
  };

  const submitPassword = async () => {
    if (pwForm.next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error('The two new passwords do not match'); return; }
    setPwBusy(true);
    try {
      await changePassword(pwForm.current, pwForm.next);
      toast.success('Password updated');
      setPwForm({ current: '', next: '', confirm: '' });
      setPwOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Could not update your password'));
    } finally { setPwBusy(false); }
  };

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Company Settings" subtitle="Your logo, chop, signature, items, tax settings, numbering and users.">
        <Button variant="outline" onClick={() => { const fresh = reset(); setForm(fresh.company); toast.success('Demo data reset'); }} data-testid="reset-demo-btn">
          <RotateCcw className="mr-1.5 h-4 w-4" /> Reset Demo
        </Button>
      </PageHeader>

      <Tabs defaultValue="company">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="company" data-testid="tab-company"><Building2 className="mr-1.5 h-4 w-4" /> Company</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding"><Stamp className="mr-1.5 h-4 w-4" /> Logo, Chop &amp; Signature</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-items"><Package className="mr-1.5 h-4 w-4" /> Products &amp; Services</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax"><Percent className="mr-1.5 h-4 w-4" /> Tax &amp; Numbering</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon className="mr-1.5 h-4 w-4" /> Users</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <Card className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Company Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
              <div><Label>Registration No.</Label><Input className="mt-1.5" value={form.regNo} onChange={(e) => set('regNo', e.target.value)} /></div>
              <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></div>
              <div><Label>Email</Label><Input className="mt-1.5" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
              <div><Label>Website</Label><Input className="mt-1.5" value={form.website} onChange={(e) => set('website', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Address</Label><Textarea className="mt-1.5" rows={2} value={form.address} onChange={(e) => set('address', e.target.value)} /></div>
              <div><Label>Bank</Label><Input className="mt-1.5" value={form.bank} onChange={(e) => set('bank', e.target.value)} /></div>
              <div><Label>Bank Account</Label><Input className="mt-1.5" value={form.bankAccount} onChange={(e) => set('bankAccount', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Terms &amp; Conditions</Label><Textarea className="mt-1.5" rows={2} value={form.terms} onChange={(e) => set('terms', e.target.value)} /></div>
            </div>
            <div className="mt-5"><Button className="bg-blue-600 hover:bg-blue-700" onClick={saveCompany} data-testid="save-company-btn"><Save className="mr-1.5 h-4 w-4" /> Save Changes</Button></div>
          </Card>
        </TabsContent>

        <TabsContent value="branding">
          <Card className="p-6">
            <p className="mb-4 text-sm text-slate-500">
              These are applied automatically to every quotation, invoice, receipt and proposal. You can still upload a different logo, chop or signature for a single document while creating it.
            </p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <ImageUploadField label="Company Logo" kind="logo" testid="settings-logo"
                hint="Shown on the document letterhead. PNG or SVG with transparent background looks best."
                value={form.logoUrl || ''} onChange={(v) => saveBranding({ logoUrl: v })} />
              <ImageUploadField label="Official Company Chop / Stamp" kind="chop" testid="settings-chop"
                hint="Your official company stamp, printed beside the signature."
                value={form.chopUrl || ''} onChange={(v) => saveBranding({ chopUrl: v })} />
              <div className="sm:col-span-2">
                <SignatureField value={form.signatureUrl || ''} onChange={(v) => saveBranding({ signatureUrl: v })} testid="settings-signature" />
              </div>
              <div><Label>Signed By (name)</Label><Input className="mt-1.5" value={form.signedBy || ''} onChange={(e) => set('signedBy', e.target.value)} placeholder="e.g. Farah Aziz" data-testid="settings-signed-by" /></div>
              <div><Label>Designation</Label><Input className="mt-1.5" value={form.signedByTitle || ''} onChange={(e) => set('signedByTitle', e.target.value)} placeholder="e.g. Managing Director" data-testid="settings-signed-title" /></div>
              <div className="sm:col-span-2"><Label>Logo Text (fallback when no logo is uploaded)</Label><Input className="mt-1.5" value={form.logoText || ''} onChange={(e) => set('logoText', e.target.value)} /></div>
            </div>
            <div className="mt-5"><Button className="bg-blue-600 hover:bg-blue-700" onClick={saveCompany} data-testid="save-branding-btn"><Save className="mr-1.5 h-4 w-4" /> Save Changes</Button></div>
          </Card>
        </TabsContent>

        <TabsContent value="items">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-100 p-4">
              <div>
                <p className="font-semibold text-slate-800">Products &amp; Services</p>
                <p className="text-xs text-slate-500">Everything here appears in the item dropdown when creating any document.</p>
              </div>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={openNewItem} data-testid="settings-add-item-btn"><Plus className="mr-1.5 h-4 w-4" /> Add Item</Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold text-slate-600">Item</TableHead>
                  <TableHead className="font-semibold text-slate-600">Unit</TableHead>
                  <TableHead className="font-semibold text-slate-600">SST</TableHead>
                  <TableHead className="text-right font-semibold text-slate-600">Price</TableHead>
                  <TableHead className="w-24 text-right font-semibold text-slate-600">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-slate-400">No products or services yet. Add your first one.</TableCell></TableRow>
                )}
                {products.map((p) => (
                  <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`settings-item-row-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600"><Package className="h-4 w-4" /></div>
                        <div>
                          <p className="font-medium text-slate-800">{p.name}</p>
                          <p className="text-xs text-slate-400">{p.description}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">/ {p.unit}</TableCell>
                    <TableCell>{p.taxable ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{company.sstRate}% SST</Badge> : <Badge variant="outline" className="text-slate-500">Exempt</Badge>}</TableCell>
                    <TableCell className="text-right font-semibold text-slate-800">{fmtRM2(p.price)}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => openEditItem(p)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600" data-testid={`settings-item-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => { removeItem('products', p.id); toast.success('Item removed'); }} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`settings-item-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="tax">
          <Card className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><Label>SST Registration No.</Label><Input className="mt-1.5" value={form.sstNo} onChange={(e) => set('sstNo', e.target.value)} /></div>
              <div><Label>SST Rate (%)</Label><Input type="number" className="mt-1.5" value={form.sstRate} onChange={(e) => set('sstRate', Number(e.target.value))} /></div>
              <div className="sm:col-span-2 mt-2"><p className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Hash className="h-4 w-4" /> Reference Number Format</p></div>
              <div className="flex items-center gap-3 sm:col-span-2">
                <Switch checked={form.includeYear !== false} onCheckedChange={(v) => set('includeYear', v)} data-testid="numbering-include-year" />
                <Label className="cursor-pointer">Include the year (e.g. INV-{new Date().getFullYear()}-0001 instead of INV-0001)</Label>
              </div>
              <div>
                <Label>Number of digits</Label>
                <Input type="number" min={1} max={8} className="mt-1.5" value={form.numberPadding ?? 4}
                  onChange={(e) => set('numberPadding', Number(e.target.value))} data-testid="numbering-padding" />
              </div>
              <div className="flex items-end">
                <p className="text-xs text-slate-500">Sample: <span className="font-mono font-semibold text-slate-700">{sampleNumber('invoices')}</span></p>
              </div>

              <div className="sm:col-span-2 mt-2"><p className="text-sm font-semibold text-slate-700">Prefix &amp; next number per document</p>
                <p className="text-xs text-slate-500">Leave "next number" blank to continue automatically from the highest existing number.</p>
              </div>
              {NUMBER_TYPES.map((t) => (
                <div key={t.key} className="rounded-xl border border-slate-200 p-3 sm:col-span-1" data-testid={`numbering-row-${t.key}`}>
                  <p className="text-sm font-medium text-slate-700">{t.label}</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Prefix</Label>
                      <Input className="mt-1" value={form[t.prefixKey] || ''} onChange={(e) => set(t.prefixKey, e.target.value)} data-testid={`numbering-prefix-${t.key}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Next number</Label>
                      <Input type="number" min={1} className="mt-1" placeholder="auto"
                        value={form.nextNumbers?.[t.key] ?? ''}
                        onChange={(e) => setNext(t.key, e.target.value)} data-testid={`numbering-next-${t.key}`} />
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">Next: <span className="font-mono">{sampleNumber(t.key)}</span></p>
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveCompany}><Save className="mr-1.5 h-4 w-4" /> Save Changes</Button>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">Ready for LHDN e-Invoice (Phase 2)</span>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="mb-4 p-6">
            <p className="font-semibold text-slate-800">Your Account</p>
            <p className="mt-0.5 text-sm text-slate-500">Signed in as <span className="font-medium text-slate-700">{user?.email}</span></p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setPwOpen(true)} data-testid="change-password-btn">
              <KeyRound className="mr-1.5 h-4 w-4" /> Change Password
            </Button>
          </Card>
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-100 p-4">
              <p className="font-semibold text-slate-800">Team Members</p>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setUserOpen(true)}><UserPlus className="mr-1.5 h-4 w-4" /> Invite User</Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold text-slate-600">Name</TableHead>
                  <TableHead className="font-semibold text-slate-600">Email</TableHead>
                  <TableHead className="font-semibold text-slate-600">Role</TableHead>
                  <TableHead className="font-semibold text-slate-600">Status</TableHead>
                  <TableHead className="text-right font-semibold text-slate-600">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{u.name.slice(0, 2).toUpperCase()}</div>
                        <span className="font-medium text-slate-800">{u.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500">{u.email}</TableCell>
                    <TableCell className="text-slate-600">{u.role}</TableCell>
                    <TableCell><StatusBadge status={u.status} /></TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => { removeItem('users', u.id); toast.success('User removed'); }} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-lg" data-testid="settings-item-dialog">
          <DialogHeader><DialogTitle>{itemEditId ? 'Edit Product / Service' : 'Add Product / Service'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Item Name</Label><Input className="mt-1.5" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} data-testid="settings-item-name" /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Input className="mt-1.5" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} data-testid="settings-item-desc" /></div>
            <div><Label>Unit</Label><Input className="mt-1.5" value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} data-testid="settings-item-unit" /></div>
            <div><Label>Price (RM)</Label><Input type="number" className="mt-1.5" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} data-testid="settings-item-price" /></div>
            <div className="flex items-center gap-3 sm:col-span-2">
              <Switch checked={itemForm.taxable} onCheckedChange={(v) => setItemForm({ ...itemForm, taxable: v })} data-testid="settings-item-taxable" />
              <Label className="cursor-pointer">Subject to {company.sstRate}% SST</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveItem} data-testid="settings-item-save">{itemEditId ? 'Save Changes' : 'Add Item'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password, then choose a new one of at least 8 characters.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2">
            <div><Label>Current Password</Label><Input type="password" className="mt-1.5" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="cp-current" /></div>
            <div><Label>New Password</Label><Input type="password" className="mt-1.5" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} placeholder="At least 8 characters" data-testid="cp-new" /></div>
            <div><Label>Confirm New Password</Label><Input type="password" className="mt-1.5" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="cp-confirm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={pwBusy} onClick={submitPassword} data-testid="cp-submit">
              {pwBusy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-md">
          <DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2">
            <div><Label>Name</Label><Input className="mt-1.5" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input className="mt-1.5" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={addUser}>Send Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
